<script setup lang="ts">
/**
 * BookingsEmbed.vue — Microsoft Bookings iFrame Embed with Fallback
 * Task: 113-111-T004
 *
 * Renders the Microsoft Bookings scheduling iFrame with:
 * - 5-second timeout-first fallback via useBookingsEmbed composable
 * - WCAG 2.1 AA: iFrame title attribute for screen readers
 * - Exact sandbox attributes per spec
 * - Responsive layout: full-width mobile, constrained via UContainer at 768px+
 * - BookingsFallback.vue rendered when hasFailed === true
 */

const BOOKINGS_URL =
  'https://outlook.office.com/book/QxReviewBookings@qiksolve.com/?ismsaljsauthenabled'

const { isLoaded, hasFailed, error, markLoaded, triggerFallback } = useBookingsEmbed()

const emit = defineEmits<{
  loaded: []
  failed: [reason: string]
}>()

watch(isLoaded, (val) => {
  if (val) emit('loaded')
})

watch(hasFailed, (val) => {
  if (val) emit('failed', error.value ?? 'Booking form unavailable')
})

function handleIframeError(): void {
  triggerFallback('Booking form unavailable')
}
</script>

<template>
  <UContainer>
    <div class="bookings-embed-wrapper">
      <!-- Bookings iFrame — hidden when fallback is active -->
      <div
        v-if="!hasFailed"
        class="relative"
      >
        <!-- Loading indicator shown while embed is loading -->
        <div
          v-if="!isLoaded"
          class="bookings-loading flex items-center justify-center py-16"
          aria-label="Loading booking calendar"
          role="status"
        >
          <span class="text-sm text-gray-500">Loading booking calendar…</span>
        </div>

        <iframe
          :src="BOOKINGS_URL"
          title="Microsoft Bookings — QxReview appointment scheduling"
          sandbox="allow-same-origin allow-forms allow-scripts allow-popups"
          loading="lazy"
          referrerpolicy="strict-origin-when-cross-origin"
          width="100%"
          :class="['bookings-iframe', { 'sr-only': !isLoaded }]"
          style="min-height: 600px; border: none; border-radius: 0.5rem;"
          @load="markLoaded"
          @error="handleIframeError"
        />
      </div>

      <!-- Fallback form rendered when embed times out or errors -->
      <BookingsFallback
        v-else
        :error-message="error ?? undefined"
      />
    </div>
  </UContainer>
</template>

<style scoped>
.bookings-iframe {
  display: block;
  width: 100%;
}

@media (max-width: 767px) {
  .bookings-iframe {
    min-height: 400px;
  }
}
</style>
